import { useState, useEffect, useCallback } from "react";
import { useApi, useAuth } from "./AuthContext";
import {
  Receipt, Upload, DollarSign, FileText, Loader2, CheckCircle2,
  Clock, XCircle, Paperclip, Eye, ClipboardList, Tag, X
} from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "./CurrencyInput";
import { formatUSD } from "../utils/currency";

const CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "materials", label: "Materials" },
  { value: "equipment", label: "Equipment" },
  { value: "supplies", label: "Supplies" },
  { value: "tolls", label: "Tolls" },
  { value: "parking", label: "Parking" },
  { value: "vehicle", label: "Vehicle" },
  { value: "other", label: "Other" },
];

const STATUS_STYLE = {
  pending:  { cls: "bg-amber-50 text-amber-700 border-amber-200",   label: "Pending",  Icon: Clock },
  approved: { cls: "bg-green-50 text-green-700 border-green-200",    label: "Approved", Icon: CheckCircle2 },
  rejected: { cls: "bg-red-50 text-red-700 border-red-200",          label: "Rejected", Icon: XCircle },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const Icon = s.Icon;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function ExpenseTrackerModule({ readOnly = false }) {
  const api = useApi();
  const { user: authUser } = useAuth();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceOrders, setServiceOrders] = useState([]);

  // Form state
  const [amount, setAmount] = useState(0);
  const [category, setCategory] = useState("fuel");
  const [soId, setSoId] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Receipt preview modal
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/field-submissions/mine");
      setSubmissions(data);
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchServiceOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/service-orders");
      setServiceOrders(data || []);
    } catch (_err) {
      // Silent: user may not have access to SOs. Linking is optional.
      setServiceOrders([]);
    }
  }, [api]);

  useEffect(() => {
    fetchMine();
    fetchServiceOrders();
  }, [fetchMine, fetchServiceOrders]);

  const resetForm = () => {
    setAmount(0);
    setCategory("fuel");
    setSoId("");
    setNotes("");
    setReceiptFile(null);
    const fileInput = document.getElementById("expense-receipt-input");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      toast.error("You don't have permission to submit");
      return;
    }
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!receiptFile) {
      toast.error("Receipt upload is required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("amount", amt);
      fd.append("category", category);
      fd.append("notes", notes);
      fd.append("service_order_id", soId);
      fd.append("receipt", receiptFile);
      await api.post("/admin/field-submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowConfirm(true);
      resetForm();
      fetchMine();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const openReceipt = async (subId) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem("admin_token");
      const res = await fetch(`${backend}/api/admin/field-submissions/${subId}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load receipt");
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      toast.error("Could not load receipt");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  };

  return (
    <div data-testid="expense-tracker-module" className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
          <Receipt className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Expense Tracker</h2>
          <p className="text-sm text-stone-500">Submit field expenses for financial approval. Your history is retained for 90 days.</p>
        </div>
      </div>

      {/* Submission Form */}
      {!readOnly && (
        <form
          onSubmit={handleSubmit}
          data-testid="expense-submission-form"
          className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm space-y-5"
        >
          <div className="flex items-center gap-2 text-stone-700 font-semibold">
            <ClipboardList className="w-4 h-4" /> New Expense Submission
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                <DollarSign className="w-3.5 h-3.5 inline mr-1" /> Amount ($)
              </label>
              <CurrencyInput
                data-testid="expense-amount-input"
                value={amount}
                onChange={(v) => setAmount(v)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                <Tag className="w-3.5 h-3.5 inline mr-1" /> Category
              </label>
              <select
                data-testid="expense-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Link to Service Order <span className="text-stone-400 font-normal">(optional)</span>
              </label>
              <select
                data-testid="expense-so-select"
                value={soId}
                onChange={(e) => setSoId(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="">— Not linked —</option>
                {serviceOrders.map((so) => (
                  <option key={so.id} value={so.id}>
                    {so.client_name} — {so.scheduled_date || "Backlog"} ({so.status})
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">
                <FileText className="w-3.5 h-3.5 inline mr-1" /> Notes
              </label>
              <textarea
                data-testid="expense-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                placeholder="Describe what this expense covers…"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-1">
                <Upload className="w-3.5 h-3.5 inline mr-1" /> Receipt <span className="text-red-500">*</span>
              </label>
              <input
                id="expense-receipt-input"
                data-testid="expense-receipt-input"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-forest-50 file:text-forest-700 file:font-medium hover:file:bg-forest-100"
                required
              />
              {receiptFile && (
                <p className="mt-1 text-xs text-stone-500 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> {receiptFile.name}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg"
            >
              Clear
            </button>
            <button
              data-testid="expense-submit-btn"
              type="submit"
              disabled={submitting || !receiptFile || !amount || Number(amount) <= 0}
              className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Submit for Approval
            </button>
          </div>
        </form>
      )}

      {/* Personal History */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <h3 className="font-semibold text-stone-800">My Submissions</h3>
            <p className="text-xs text-stone-500">Visible for 90 days. Older entries remain in the financial records.</p>
          </div>
          <span className="text-xs text-stone-400">{submissions.length} entries</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-stone-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="px-5 py-12 text-center text-stone-400 text-sm">
            No submissions yet. Submit your first field expense above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="my-submissions-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50 text-stone-600">
                  <th className="text-left px-4 py-2.5 font-medium">Date</th>
                  <th className="text-left px-4 py-2.5 font-medium">Category</th>
                  <th className="text-right px-4 py-2.5 font-medium">Amount</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Notes</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} data-testid={`submission-row-${s.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-2.5 text-stone-600 whitespace-nowrap">{formatDate(s.created_at)}</td>
                    <td className="px-4 py-2.5 capitalize text-stone-700">{s.category}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-stone-800">{formatUSD(s.amount || 0)}</td>
                    <td className="px-4 py-2.5 text-stone-600 hidden md:table-cell max-w-xs truncate" title={s.notes}>
                      {s.notes || "—"}
                      {s.status === "rejected" && s.reject_reason && (
                        <div className="text-xs text-red-600 mt-0.5">Reason: {s.reject_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5"><StatusPill status={s.status} /></td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        data-testid={`view-receipt-${s.id}`}
                        onClick={() => openReceipt(s.id)}
                        className="inline-flex items-center gap-1 text-forest-700 hover:text-forest-900 text-xs font-semibold"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Success Confirmation Modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowConfirm(false)}
          data-testid="expense-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 bg-green-100 rounded-full mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-1">Expense submitted.</h3>
            <p className="text-sm text-stone-500 mb-5">Awaiting financial approval.</p>
            <button
              data-testid="expense-confirm-close"
              onClick={() => setShowConfirm(false)}
              className="w-full bg-forest-600 hover:bg-forest-700 text-white py-2.5 rounded-lg text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Receipt Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="receipt-preview-modal"
        >
          <div className="relative max-w-3xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closePreview}
              className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow"
            >
              <X className="w-4 h-4" />
            </button>
            <img src={previewUrl} alt="Receipt" className="w-full h-full max-h-[90vh] object-contain rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
